import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';

const TermsOfService = () => {
  const lastUpdated = "January 13, 2026";
  const effectiveDate = "January 13, 2026";

  return (
    <div className="min-h-screen bg-white">
      <Header isAuthenticated={false} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Terms of Service
          </h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <div>Last updated: {lastUpdated}</div>
            <div>•</div>
            <div>Effective: {effectiveDate}</div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-8">
          <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8 rounded-r-lg">
            <p className="text-gray-800 font-medium mb-2">
              Welcome to WanderVibe!
            </p>
            <p className="text-gray-700 text-sm">
              These Terms of Service ("Terms") govern your use of WanderVibe, a women's travel safety platform. By accessing or using our service, you agree to be bound by these Terms. Please read them carefully.
            </p>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8 rounded-r-lg">
            <p className="font-semibold text-gray-900 mb-2">⚠️ Important</p>
            <p className="text-gray-700 text-sm">
              By using WanderVibe, you agree to these Terms and our <Link to="/privacy" className="text-teal-600 hover:underline font-medium">Privacy Policy</Link>. If you don't agree, please don't use our service.
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <a href="#acceptance" className="text-teal-600 hover:text-teal-700 hover:underline">1. Acceptance of Terms</a>
              <a href="#eligibility" className="text-teal-600 hover:text-teal-700 hover:underline">2. Eligibility</a>
              <a href="#account" className="text-teal-600 hover:text-teal-700 hover:underline">3. Account Registration</a>
              <a href="#subscription" className="text-teal-600 hover:text-teal-700 hover:underline">4. Subscription & Payment</a>
              <a href="#user-content" className="text-teal-600 hover:text-teal-700 hover:underline">5. User Content</a>
              <a href="#prohibited" className="text-teal-600 hover:text-teal-700 hover:underline">6. Prohibited Conduct</a>
              <a href="#safety" className="text-teal-600 hover:text-teal-700 hover:underline">7. Safety Features</a>
              <a href="#disclaimer" className="text-teal-600 hover:text-teal-700 hover:underline">8. Disclaimers</a>
              <a href="#limitation" className="text-teal-600 hover:text-teal-700 hover:underline">9. Limitation of Liability</a>
              <a href="#termination" className="text-teal-600 hover:text-teal-700 hover:underline">10. Termination</a>
              <a href="#changes" className="text-teal-600 hover:text-teal-700 hover:underline">11. Changes to Terms</a>
              <a href="#contact" className="text-teal-600 hover:text-teal-700 hover:underline">12. Contact</a>
            </div>
          </div>

          {/* 1. Acceptance of Terms */}
          <section id="acceptance" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              1. Acceptance of Terms
            </h2>

            <p className="text-gray-700 mb-4">
              By creating an account, accessing our website, or using our mobile application, you acknowledge that you have read, understood, and agree to be bound by these Terms and all applicable laws and regulations.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">This Agreement Includes:</h4>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>These Terms of Service</li>
                <li>Our <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link></li>
                <li>Our Community Guidelines (if applicable)</li>
                <li>Any additional terms presented during subscription purchase</li>
              </ul>
            </div>
          </section>

          {/* 2. Eligibility */}
          <section id="eligibility" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              2. Eligibility
            </h2>

            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="font-semibold text-gray-900 mb-2">Age Requirement</p>
                <p className="text-sm text-gray-700">
                  You must be at least 18 years old to use WanderVibe. By using our service, you represent and warrant that you are 18 years of age or older.
                </p>
              </div>

              <p className="text-gray-700">
                You must also:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li>Have the legal capacity to enter into a binding contract</li>
                <li>Not be prohibited from using the service under any applicable laws</li>
                <li>Comply with all local laws regarding online conduct and acceptable content</li>
                <li>Provide accurate and truthful information during registration</li>
              </ul>
            </div>
          </section>

          {/* 3. Account Registration */}
          <section id="account" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              3. Account Registration & Security
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1 Account Creation</h3>
            <p className="text-gray-700 mb-4">
              To access certain features, you must create an account by providing:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1 mb-4">
              <li>A valid email address</li>
              <li>A secure password</li>
              <li>A username</li>
              <li>Any other information required during signup</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Account Security</h3>
            <div className="bg-purple-50 p-4 rounded-lg mb-4">
              <p className="font-semibold text-gray-900 mb-2">You are responsible for:</p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>Maintaining the confidentiality of your password</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
                <li>Ensuring your account information remains accurate and up-to-date</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3 Account Restrictions</h3>
            <p className="text-gray-700">
              You may not:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Create an account for anyone other than yourself</li>
              <li>Use another person's account without permission</li>
              <li>Create multiple accounts to evade restrictions</li>
              <li>Sell, trade, or transfer your account</li>
            </ul>
          </section>

          {/* 4. Subscription & Payment */}
          <section id="subscription" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              4. Subscription & Payment Terms
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1 Subscription Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-1">Free Tier</h4>
                <p className="text-sm text-gray-700">Basic features at no cost</p>
              </div>
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-300">
                <h4 className="font-semibold text-teal-900 mb-1">Plus Tier</h4>
                <p className="text-sm text-gray-700">$9.99/month - Enhanced features</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-300">
                <h4 className="font-semibold text-purple-900 mb-1">Pro Tier</h4>
                <p className="text-sm text-gray-700">$29.99/month - Premium features</p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Payment Processing</h3>
            <ul className="list-disc ml-6 text-gray-700 space-y-2 mb-4">
              <li>Subscriptions are processed through Stripe, a third-party payment processor</li>
              <li>By subscribing, you authorize us to charge your payment method</li>
              <li>All prices are in USD unless otherwise stated</li>
              <li>Prices are subject to change with 30 days notice</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Billing & Renewal</h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mb-4">
              <p className="font-semibold text-gray-900 mb-2">Auto-Renewal</p>
              <p className="text-sm text-gray-700">
                Subscriptions automatically renew at the end of each billing period (monthly or annually) unless you cancel before the renewal date. You will be charged the then-current price for your subscription tier.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.4 Cancellation & Refunds</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Cancellation:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>You may cancel your subscription at any time from your account settings</li>
                  <li>Cancellation takes effect at the end of your current billing period</li>
                  <li>You'll retain access to paid features until the end of the paid period</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Refunds:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>We offer a 7-day money-back guarantee for new subscriptions</li>
                  <li>Refund requests after 7 days are evaluated case-by-case</li>
                  <li>No refunds for partial months or unused time</li>
                  <li>Contact <a href="mailto:support@wandervibe.com" className="text-teal-600 hover:underline">support@wandervibe.com</a> for refund requests</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.5 Free Trial (if applicable)</h3>
            <p className="text-gray-700 text-sm">
              If you sign up for a free trial, you will not be charged until the trial period ends. You may cancel anytime during the trial to avoid charges. After the trial ends, you will automatically be charged for the subscription unless you cancel.
            </p>
          </section>

          {/* 5. User Content */}
          <section id="user-content" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              5. User-Generated Content
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.1 Your Content</h3>
            <p className="text-gray-700 mb-3">
              "User Content" means any content you post, upload, share, or transmit through WanderVibe, including:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1 mb-4">
              <li>Reviews and ratings of locations</li>
              <li>Comments and forum posts</li>
              <li>Photos and videos</li>
              <li>Trip plans and itineraries</li>
              <li>Safety reports and feedback</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 License to WanderVibe</h3>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-gray-700 mb-2">
                By posting User Content, you grant WanderVibe a:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li><strong>Non-exclusive</strong> (you still own your content)</li>
                <li><strong>Worldwide</strong> (we can use it globally)</li>
                <li><strong>Royalty-free</strong> (we don't pay you for use)</li>
                <li><strong>Transferable and sublicensable</strong> (we can let partners use it)</li>
                <li><strong>Perpetual</strong> (license doesn't expire, even if you delete your account)</li>
              </ul>
              <p className="text-sm text-gray-600 mt-3 italic">
                This license allows us to display, distribute, and promote your content on WanderVibe and in our marketing materials.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.3 Content Ownership & Rights</h3>
            <p className="text-gray-700 mb-3">
              You represent and warrant that:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>You own or have the right to use all User Content you post</li>
              <li>Your content doesn't violate any third-party rights</li>
              <li>Your content is accurate to the best of your knowledge</li>
              <li>You have permission to share any photos of others</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.4 Content Standards</h3>
            <div className="bg-pink-50 border-l-4 border-pink-500 p-4 rounded-r-lg">
              <p className="font-semibold text-gray-900 mb-2">Your content must NOT:</p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>Contain false, misleading, or fraudulent information</li>
                <li>Violate any laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Contain hate speech, harassment, or discrimination</li>
                <li>Include personal information of others without consent</li>
                <li>Contain malware, viruses, or harmful code</li>
                <li>Be sexually explicit or inappropriate</li>
                <li>Promote illegal activities or violence</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.5 Content Moderation</h3>
            <p className="text-gray-700">
              We reserve the right to:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>Review, monitor, or remove any User Content</li>
              <li>Suspend or terminate accounts that violate these Terms</li>
              <li>Report illegal content to authorities</li>
              <li>Edit or remove content without notice</li>
            </ul>
          </section>

          {/* 6. Prohibited Conduct */}
          <section id="prohibited" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              6. Prohibited Conduct
            </h2>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-4">
              <p className="font-semibold text-gray-900 mb-2">You agree NOT to:</p>

              <div className="space-y-3 mt-3">
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">🚫 Misuse the Service:</h4>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Use automated systems (bots, scrapers) without permission</li>
                    <li>Reverse engineer, decompile, or disassemble the service</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with or disrupt the service</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">🚫 Abuse Safety Features:</h4>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Submit false safety reports or alerts</li>
                    <li>Misuse emergency SOS features</li>
                    <li>Provide fake reviews or ratings</li>
                    <li>Manipulate safety scores</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">🚫 Harm Others:</h4>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Harass, threaten, or bully other users</li>
                    <li>Impersonate others</li>
                    <li>Share others' personal information without consent</li>
                    <li>Engage in discriminatory behavior</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">🚫 Commercial Misuse:</h4>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Use the service for unauthorized commercial purposes</li>
                    <li>Spam or send unsolicited messages</li>
                    <li>Advertise without permission</li>
                    <li>Collect user data for commercial use</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-700 italic">
              Violation of these prohibitions may result in immediate account termination and legal action.
            </p>
          </section>

          {/* 7. Safety Features & Emergency Services */}
          <section id="safety" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              7. Safety Features & Emergency Services
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-4">
              <p className="font-semibold text-gray-900 mb-3 text-lg">⚠️ IMPORTANT SAFETY DISCLAIMER</p>

              <div className="space-y-3 text-gray-800">
                <p className="font-medium">
                  WanderVibe is NOT an emergency service and should NOT replace official emergency contacts (911, local police, etc.).
                </p>

                <p className="text-sm">
                  Our safety features (ratings, alerts, SOS) are community-driven and informational only. They should be used as one resource among many for travel planning and safety awareness.
                </p>

                <div className="mt-4">
                  <p className="font-semibold mb-2">In a Real Emergency:</p>
                  <ul className="list-disc ml-6 space-y-1 text-sm">
                    <li>Call local emergency services (911 in US, 112 in EU, etc.)</li>
                    <li>Contact your country's embassy or consulate</li>
                    <li>Use WanderVibe's SOS feature as a supplement, not replacement</li>
                  </ul>
                </div>

                <p className="text-sm italic mt-4">
                  WanderVibe is not responsible for the outcome of any emergency situation. Our features are designed to supplement, not replace, professional emergency services.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Understanding Our Safety Features</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Safety Ratings:</h4>
                <p className="text-sm text-gray-700">
                  Based on user reviews and may not reflect current conditions. Always verify with official sources.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Emergency SOS:</h4>
                <p className="text-sm text-gray-700">
                  Alerts your emergency contacts and nearby users. Does not contact official emergency services.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Safety Alerts:</h4>
                <p className="text-sm text-gray-700">
                  Community-reported and may not be verified. Use as informational guidance only.
                </p>
              </div>
            </div>
          </section>

          {/* 8. Disclaimers */}
          <section id="disclaimer" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              8. Disclaimers
            </h2>

            <div className="bg-gray-100 p-6 rounded-lg border-2 border-gray-300">
              <p className="font-semibold text-gray-900 mb-4 text-lg uppercase">SERVICE PROVIDED "AS IS"</p>

              <div className="space-y-4 text-sm text-gray-800">
                <p>
                  WANDERVIBE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                </p>

                <ul className="list-disc ml-6 space-y-2">
                  <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                  <li>Warranties that the service will be uninterrupted, secure, or error-free</li>
                  <li>Warranties regarding the accuracy, reliability, or completeness of content</li>
                  <li>Warranties that defects will be corrected</li>
                </ul>

                <p className="mt-4 font-medium">
                  We do not guarantee that:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Safety ratings and reviews are accurate or current</li>
                  <li>Emergency features will work at all times</li>
                  <li>User-generated content is truthful or reliable</li>
                  <li>The service will meet your specific needs</li>
                </ul>

                <p className="mt-4 italic">
                  Your use of WanderVibe is at your own risk. You are solely responsible for your safety while traveling.
                </p>
              </div>
            </div>
          </section>

          {/* 9. Limitation of Liability */}
          <section id="limitation" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              9. Limitation of Liability
            </h2>

            <div className="bg-gray-100 p-6 rounded-lg border-2 border-gray-300">
              <p className="font-semibold text-gray-900 mb-4 text-lg uppercase">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>

              <div className="space-y-4 text-sm text-gray-800">
                <p>
                  WanderVibe, its officers, directors, employees, and agents shall NOT be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
                </p>

                <ul className="list-disc ml-6 space-y-2">
                  <li>Loss of profits, revenue, data, or goodwill</li>
                  <li>Injury, death, or harm resulting from use of the service</li>
                  <li>Decisions made based on our safety ratings or reviews</li>
                  <li>Failure of emergency features to function properly</li>
                  <li>Actions or inactions of other users</li>
                  <li>Service interruptions or errors</li>
                  <li>Unauthorized access to your account or data</li>
                </ul>

                <div className="mt-6 p-4 bg-white rounded border border-gray-300">
                  <p className="font-semibold text-gray-900 mb-2">Maximum Liability Cap</p>
                  <p>
                    Our total liability to you for any claims arising out of or related to these Terms or your use of WanderVibe shall not exceed the greater of:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>$100 USD, or</li>
                    <li>The amount you paid us in the 12 months before the claim</li>
                  </ul>
                </div>

                <p className="mt-4 font-medium">
                  Some jurisdictions do not allow the exclusion or limitation of certain damages. In such jurisdictions, our liability is limited to the maximum extent permitted by law.
                </p>
              </div>
            </div>
          </section>

          {/* 10. Termination */}
          <section id="termination" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              10. Termination
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.1 Termination by You</h3>
            <p className="text-gray-700 mb-3">
              You may terminate your account at any time by:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1 mb-4">
              <li>Going to Settings → Privacy → Delete Account</li>
              <li>Emailing <a href="mailto:support@wandervibe.com" className="text-teal-600 hover:underline">support@wandervibe.com</a></li>
            </ul>
            <p className="text-sm text-gray-600 italic">
              Account deletion is permanent and cannot be undone. Your data will be deleted within 30 days per our Privacy Policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.2 Termination by WanderVibe</h3>
            <p className="text-gray-700 mb-3">
              We may suspend or terminate your account immediately if:
            </p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1 mb-4">
              <li>You violate these Terms</li>
              <li>You engage in prohibited conduct</li>
              <li>Your account is used for illegal activities</li>
              <li>You provide false information</li>
              <li>Your payment method fails and you don't update it</li>
              <li>We're required to do so by law</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.3 Effect of Termination</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">Upon termination:</p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>Your right to use WanderVibe immediately ceases</li>
                <li>We may delete your account and data</li>
                <li>Paid subscriptions are non-refundable (except as required by law)</li>
                <li>Sections of these Terms that should survive (e.g., disclaimers, limitations) remain in effect</li>
              </ul>
            </div>
          </section>

          {/* 11. Changes to Terms */}
          <section id="changes" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              11. Changes to These Terms
            </h2>

            <p className="text-gray-700 mb-4">
              We may modify these Terms at any time. When we do:
            </p>

            <div className="bg-teal-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Notification Process:</h4>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>We'll update the "Last Updated" date</li>
                <li>For material changes, we'll email you at least 30 days in advance</li>
                <li>We'll display a notice on our website</li>
                <li>You may be required to accept the new Terms to continue using the service</li>
              </ul>
            </div>

            <p className="text-gray-700">
              If you don't agree to the modified Terms, you must stop using WanderVibe and may cancel your subscription for a prorated refund.
            </p>
          </section>

          {/* 12. General Provisions */}
          <section id="general" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              12. General Provisions
            </h2>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Governing Law:</h4>
                <p className="text-sm text-gray-700">
                  These Terms are governed by the laws of [Your State/Country], without regard to conflict of law principles.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Dispute Resolution:</h4>
                <p className="text-sm text-gray-700">
                  Any disputes shall be resolved through binding arbitration in [Your Location], except where prohibited by law. You waive your right to participate in class action lawsuits.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Severability:</h4>
                <p className="text-sm text-gray-700">
                  If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Entire Agreement:</h4>
                <p className="text-sm text-gray-700">
                  These Terms, along with our Privacy Policy, constitute the entire agreement between you and WanderVibe.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Waiver:</h4>
                <p className="text-sm text-gray-700">
                  Our failure to enforce any provision of these Terms does not waive our right to enforce it later.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Assignment:</h4>
                <p className="text-sm text-gray-700">
                  You may not assign these Terms. We may assign our rights and obligations to any party.
                </p>
              </div>
            </div>
          </section>

          {/* 13. Contact */}
          <section id="contact" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              13. Contact Us
            </h2>

            <p className="text-gray-700 mb-4">
              If you have questions about these Terms:
            </p>

            <div className="bg-teal-50 p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Contact Information:</h4>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-800">Legal Team:</span>{' '}
                  <a href="mailto:legal@wandervibe.com" className="text-teal-600 hover:underline">
                    legal@wandervibe.com
                  </a>
                </div>

                <div>
                  <span className="font-semibold text-gray-800">Support:</span>{' '}
                  <a href="mailto:support@wandervibe.com" className="text-teal-600 hover:underline">
                    support@wandervibe.com
                  </a>
                </div>

                <div>
                  <span className="font-semibold text-gray-800">Mailing Address:</span>
                  <div className="ml-4 mt-1 text-gray-700">
                    WanderVibe, Inc.<br />
                    Legal Department<br />
                    [Address Line 1]<br />
                    [City, State ZIP]<br />
                    United States
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Back to Top */}
          <div className="text-center mt-8 pt-6 border-t border-gray-200">
            <a
              href="#top"
              className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              Back to Top
            </a>
          </div>

          {/* Legal Links */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600 mb-3">Related Legal Documents:</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>
              <span className="text-gray-400">|</span>
              <Link to="/cookies" className="text-teal-600 hover:underline">Cookie Policy</Link>
              <span className="text-gray-400">|</span>
              <Link to="/" className="text-teal-600 hover:underline">Home</Link>
            </div>
          </div>

          {/* Acknowledgment */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg text-center">
            <p className="text-sm text-gray-700">
              By using WanderVibe, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
